import { useState, useEffect } from "react";
import {
  Star,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  Trash2,
  Eye,
  MessageSquareQuote,
  Filter,
} from "lucide-react";
import apiClient from "@/lib/api-client";
import { useConfirm } from "@/contexts/ConfirmContext";

interface Testimonial {
  id: string;
  authorName: string;
  businessName: string;
  content: string;
  rating: number;
  status: string;
  rejectionReason: string | null;
  createdAt: string;
  reviewedAt: string | null;
  user: { firstName: string; lastName: string; email: string };
  organization: { name: string; slug: string };
}

export default function AdminTestimonials() {
  const { confirm } = useConfirm();
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");

  useEffect(() => {
    loadTestimonials();
  }, [statusFilter]);

  const loadTestimonials = async () => {
    try {
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const res = await apiClient.get(`/admin/testimonials${params}`);
      setTestimonials(res.data.data);
    } catch {
      // Error
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id: string) => {
    setActionLoading(id);
    try {
      await apiClient.put(`/admin/testimonials/${id}/review`, {
        status: "approved",
      });
      await loadTestimonials();
    } catch {
      // Error
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (id: string) => {
    setActionLoading(id);
    try {
      await apiClient.put(`/admin/testimonials/${id}/review`, {
        status: "rejected",
        rejectionReason: rejectionReason || undefined,
      });
      setRejectId(null);
      setRejectionReason("");
      await loadTestimonials();
    } catch {
      // Error
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!(await confirm({ title: "¿Eliminar este testimonio?", message: "Esta acción no se puede deshacer.", confirmText: "Sí, eliminar", danger: true }))) return;
    setActionLoading(id);
    try {
      await apiClient.delete(`/admin/testimonials/${id}`);
      setTestimonials((prev) => prev.filter((t) => t.id !== id));
    } catch {
      // Error
    } finally {
      setActionLoading(null);
    }
  };

  const statusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <Clock className="w-3 h-3" />
            Pendiente
          </span>
        );
      case "approved":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3" />
            Aprobado
          </span>
        );
      case "rejected":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="w-3 h-3" />
            Rechazado
          </span>
        );
      default:
        return null;
    }
  };

  const pendingCount = testimonials.filter(
    (t) => t.status === "pending",
  ).length;

  return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">
      {/* Header */}
      <div className="mb-4 md:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">
            Testimonios
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Gestiona las valoraciones de los clientes
            {pendingCount > 0 && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                {pendingCount} pendiente{pendingCount > 1 ? "s" : ""}
              </span>
            )}
          </p>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => {
              setLoading(true);
              setStatusFilter(e.target.value);
            }}
            className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">Todos</option>
            <option value="pending">Pendientes</option>
            <option value="approved">Aprobados</option>
            <option value="rejected">Rechazados</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : testimonials.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <MessageSquareQuote className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">
            No hay testimonios
          </h3>
          <p className="text-gray-500 text-sm">
            {statusFilter !== "all"
              ? `No hay testimonios con estado "${statusFilter}".`
              : "Aún no se han recibido testimonios de clientes."}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {testimonials.map((t) => (
            <div
              key={t.id}
              className={`bg-white rounded-xl shadow-sm border overflow-hidden ${
                t.status === "pending" ? "border-yellow-200" : "border-gray-200"
              }`}
            >
              <div className="p-4 md:p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    {/* Author info */}
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 font-bold text-sm flex-shrink-0">
                        {t.authorName
                          .split(" ")
                          .map((w) => w[0])
                          .join("")
                          .toUpperCase()
                          .slice(0, 2)}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 text-sm truncate">
                          {t.authorName}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {t.organization.name} &middot; {t.user.email}
                        </p>
                      </div>
                      {statusBadge(t.status)}
                    </div>

                    {/* Rating */}
                    <div className="flex items-center gap-1 mb-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`w-4 h-4 ${
                            star <= t.rating
                              ? "text-yellow-400 fill-yellow-400"
                              : "text-gray-300"
                          }`}
                        />
                      ))}
                    </div>

                    {/* Content */}
                    <p className="text-gray-700 italic">
                      &ldquo;{t.content}&rdquo;
                    </p>

                    {/* Rejection reason */}
                    {t.status === "rejected" && t.rejectionReason && (
                      <p className="mt-2 text-xs text-red-600">
                        Motivo: {t.rejectionReason}
                      </p>
                    )}

                    {/* Date */}
                    <p className="mt-2 text-xs text-gray-400">
                      Enviado el{" "}
                      {new Date(t.createdAt).toLocaleDateString("es-ES", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                      {t.reviewedAt && (
                        <>
                          {" "}
                          &middot; Revisado el{" "}
                          {new Date(t.reviewedAt).toLocaleDateString("es-ES", {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          })}
                        </>
                      )}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    {t.status === "pending" && (
                      <>
                        <button
                          onClick={() => handleApprove(t.id)}
                          disabled={actionLoading === t.id}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                        >
                          {actionLoading === t.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <CheckCircle className="w-3.5 h-3.5" />
                          )}
                          Aprobar
                        </button>
                        <button
                          onClick={() => setRejectId(t.id)}
                          disabled={actionLoading === t.id}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                        >
                          <XCircle className="w-3.5 h-3.5" />
                          Rechazar
                        </button>
                      </>
                    )}
                    {t.status === "approved" && (
                      <a
                        href="https://www.reservamanager.com/#testimonials"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        Ver en web
                      </a>
                    )}
                    <button
                      onClick={() => handleDelete(t.id)}
                      disabled={actionLoading === t.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Eliminar
                    </button>
                  </div>
                </div>

                {/* Reject modal inline */}
                {rejectId === t.id && (
                  <div className="mt-4 p-4 bg-red-50 rounded-lg border border-red-100">
                    <p className="text-sm font-medium text-red-800 mb-2">
                      Motivo del rechazo (opcional)
                    </p>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      rows={2}
                      placeholder="Explica al usuario por qué se rechaza su testimonio..."
                      className="w-full px-3 py-2 text-sm border border-red-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                    />
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => handleReject(t.id)}
                        disabled={actionLoading === t.id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                      >
                        {actionLoading === t.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <XCircle className="w-3.5 h-3.5" />
                        )}
                        Confirmar rechazo
                      </button>
                      <button
                        onClick={() => {
                          setRejectId(null);
                          setRejectionReason("");
                        }}
                        className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
